import type {LocationContext, LocationMetaContents} from "@ots-frontend/ots-data";
import {useMemo} from "react";

/**
 * Made-up units for trying the live map without real pings: each sits a mile or two from one of the region's sites
 * and carries one of the load states the map draws. Dev only; ids start with "mock_" so they never match real ones.
 */
export type MockFleetEntityType = "PERSON" | "TRAILER" | "VEHICLE";

type MockSite = {location?: {latCoord: number; longCoord: number}};

type Scenario = {
  name: string;
  status: "MOVING" | "IDLE" | "AT_SITE";
  /** Minutes since the ping; past the stale setting it fades. */
  pingAgeMinutes?: number;
  contents?: Partial<LocationMetaContents>;
};

const BBL = {quantityUom: "barrels", capacity: 130, capacityUom: "barrels"};
const WATER = {...BBL, category: "WATER", materialType: "Produced Water"};
const OIL = {...BBL, category: "OIL", materialType: "Crude"};

/** Every state the ring draws, plus the ones it leaves bare. */
export const MOCK_FLEET_SCENARIOS: readonly Scenario[] = [
  {name: "Empty", status: "MOVING", contents: {state: "EMPTY"}},
  {name: "Water 35%", status: "MOVING", contents: {...WATER, quantity: 45}},
  {name: "Water 70%", status: "AT_SITE", contents: {...WATER, quantity: 91}},
  {name: "Water 95%", status: "MOVING", contents: {...WATER, quantity: 124}},
  {name: "Oil 50%", status: "IDLE", contents: {...OIL, quantity: 65}},
  {name: "Oil 92%", status: "MOVING", contents: {...OIL, quantity: 120}},
  {name: "Estimated 60%", status: "MOVING", contents: {...WATER, quantity: 78, estimated: true}},
  {name: "Over capacity", status: "AT_SITE", contents: {...WATER, quantity: 146}},
  {name: "Unknown", status: "IDLE", contents: {state: "UNKNOWN"}},
  {name: "No contents", status: "MOVING"},
  {name: "Stale, water 80%", status: "IDLE", pingAgeMinutes: 120, contents: {...WATER, quantity: 104}},
  {name: "Mixed 50%", status: "MOVING", contents: {...WATER, quantity: 65, materialType: "Mixed"}},
];

const PREFIX: Record<MockFleetEntityType, string> = {PERSON: "Mock driver", TRAILER: "MOCK-TRL", VEHICLE: "MOCK-TRK"};
const MILE_IN_DEGREES = 1 / 69;

/** A small seeded generator, so the fleet sits still between renders and reloads. */
const seeded = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
};

export type MockFleetOptions = {
  entityType: MockFleetEntityType;
  sites: readonly MockSite[];
  now: number;
  /** Units to make; the scenarios repeat past their count. */
  count?: number;
};

export const mockFleetLocations = ({entityType, sites, now, count = MOCK_FLEET_SCENARIOS.length}: MockFleetOptions): LocationContext[] => {
  const anchors = sites.flatMap(({location}) => location ? [location] : []);
  if (!anchors.length) return [];
  const random = seeded(anchors.length * 7919 + count);
  return Array.from({length: count}, (_, index) => {
    const scenario = MOCK_FLEET_SCENARIOS[index % MOCK_FLEET_SCENARIOS.length];
    const site = anchors[Math.floor(random() * anchors.length)];
    // On site means on top of it; otherwise a mile or three off in any direction.
    const miles = scenario.status === "AT_SITE" ? 0.05 : 1 + random() * 2;
    const angle = random() * 2 * Math.PI;
    const latitude = site.latCoord + Math.sin(angle) * miles * MILE_IN_DEGREES;
    const longitude = site.longCoord + Math.cos(angle) * miles * MILE_IN_DEGREES / Math.cos(site.latCoord * Math.PI / 180);
    const pingedAt = now - (scenario.pingAgeMinutes ?? Math.floor(random() * 3)) * 60_000;
    const number = String(index + 1).padStart(2, "0");
    return {
      entityId: `mock_${entityType.toLowerCase()}_${number}`,
      entityType,
      entityName: `${PREFIX[entityType]} ${number} · ${scenario.name}`,
      tenantCode: "mock",
      location: {latitude, longitude, dttm: pingedAt, speed: scenario.status === "MOVING" ? 18 : 0, bearing: Math.round(random() * 360)},
      meta: {
        activity: {inShift: true, onSite: scenario.status === "AT_SITE", status: scenario.status, observedDttm: pingedAt},
        ...(scenario.contents && {
          contents: {state: "PRESENT", estimated: false, calculatedDttm: pingedAt - 30_000, ...scenario.contents},
        }),
      },
    } as LocationContext;
  });
};

export const MOCK_FLEET_STORAGE_KEY = "ots.live-map.mock-fleet";

/**
 * How many mock units to add: `?mockFleet` (or `?mockFleet=40`) in the URL, or the same value saved under
 * `MOCK_FLEET_STORAGE_KEY`; 0 when neither is set.
 */
export const readMockFleetCount = (): number => {
  const parse = (value: string | null) => {
    if (value == null) return 0;
    const count = Number(value);
    return value === "" || value === "true" ? MOCK_FLEET_SCENARIOS.length : Number.isInteger(count) && count > 0 ? Math.min(count, 500) : 0;
  };
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("mockFleet");
    return parse(fromUrl ?? window.localStorage.getItem(MOCK_FLEET_STORAGE_KEY));
  } catch {
    return 0;
  }
};

/**
 * The tracked units plus the mock ones when asked for. Pass `enabled` as the app's dev flag, so a production build
 * never reads the switch.
 */
export const useMockFleetLocations = (
  tracked: LocationContext[] | undefined,
  {enabled, ...options}: Omit<MockFleetOptions, "count"> & {enabled: boolean},
): LocationContext[] | undefined => {
  const count = useMemo(() => enabled ? readMockFleetCount() : 0, [enabled]);
  const {entityType, sites, now} = options;
  return useMemo(() => {
    if (!count) return tracked;
    return [...(tracked ?? []), ...mockFleetLocations({entityType, sites, now, count})];
  }, [count, tracked, entityType, sites, now]);
};
